import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSplash } from '../../contexts/SplashContext';
import { useAppTheme } from '../../contexts/ThemeContext';
import { LOGO_ASSETS, LOGO_RATIO } from './Logo';

const HEIGHT = 120;
const WIDTH = Math.round(HEIGHT * LOGO_RATIO);
const TOTAL_MS = 2600;

/**
 * Full-screen intro that plays on every page load:
 *  1. brand rings ripple out from the centre
 *  2. the lavender letters rise into place
 *  3. the sky accent pops in
 *  4. the navy drop falls in with a bounce and a splash ring
 *  5. the tagline fades in, then the curtain lifts and the logo flies to the header
 */
const Splash: React.FC = () => {
  const { done, finish } = useSplash();
  const { reduceMotion } = useAppTheme();
  const theme = useTheme();
  const { t } = useTranslation();
  const [landed, setLanded] = useState(false);

  useEffect(() => {
    if (done) return;
    const ms = reduceMotion ? 250 : TOTAL_MS;
    const id = window.setTimeout(finish, ms);
    return () => window.clearTimeout(id);
  }, [done, finish, reduceMotion]);

  const dark = theme.palette.mode === 'dark';
  const ring = (color: string, delay: number) => (
    <motion.span
      key={color}
      initial={{ scale: 0.2, opacity: 0.9 }}
      animate={{ scale: 3.2, opacity: 0 }}
      transition={{ duration: 1.6, delay, ease: 'easeOut' }}
      style={{
        position: 'absolute', width: 220, height: 220, borderRadius: '50%',
        border: `3px solid ${color}`, left: 'calc(50% - 110px)', top: 'calc(50% - 110px)',
      }}
    />
  );

  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          key="splash"
          role="dialog"
          aria-label="Nadeem"
          initial={{ opacity: 1 }}
          exit={{ y: '-100%', borderBottomLeftRadius: '50% 12%', borderBottomRightRadius: '50% 12%' }}
          transition={{ duration: 0.8, ease: [0.76, 0, 0.24, 1] }}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000, overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: dark ? theme.palette.brand.bgDark : theme.palette.brand.bgLight,
          }}
        >
          {!reduceMotion && (
            <>
              {ring(theme.palette.brand.lavender, 0)}
              {ring(theme.palette.brand.sky, 0.25)}
              {ring(theme.palette.brand.navy, 0.5)}
            </>
          )}

          <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
            <motion.div layoutId="brand-logo" style={{ position: 'relative', width: WIDTH, height: HEIGHT }}>
              <motion.img
                src={LOGO_ASSETS.letters}
                alt=""
                initial={{ opacity: 0, y: 50, filter: 'blur(14px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                transition={{ delay: 0.25, duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
              />
              <motion.img
                src={LOGO_ASSETS.accent}
                alt=""
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.85, type: 'spring', stiffness: 380, damping: 14 }}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transformOrigin: '22% 55%' }}
              />
              <motion.img
                src={LOGO_ASSETS.drop}
                alt=""
                initial={{ opacity: 0, y: -160, rotate: -25 }}
                animate={{ opacity: 1, y: 0, rotate: 0 }}
                transition={{ delay: 1.0, type: 'spring', stiffness: 260, damping: 13, mass: 0.9 }}
                onAnimationComplete={() => setLanded(true)}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transformOrigin: '90% 60%' }}
              />
              {landed && !reduceMotion && (
                <motion.span
                  initial={{ scale: 0.4, opacity: 0.7 }}
                  animate={{ scale: 2.6, opacity: 0 }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                  style={{
                    position: 'absolute', width: 60, height: 60, borderRadius: '50%',
                    border: `2px solid ${theme.palette.brand.navy}`, right: '4%', top: '38%',
                  }}
                />
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.55, duration: 0.6 }}
            >
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', letterSpacing: '0.3em', fontSize: '0.8rem', display: 'block', textAlign: 'center' }}
              >
                {t('app.tagline')}
              </Typography>
            </motion.div>
          </Box>

          <Button
            onClick={finish}
            size="small"
            variant="text"
            sx={{ position: 'absolute', bottom: 24, insetInlineEnd: 24, color: 'text.secondary', opacity: 0.7 }}
          >
            {t('common.skip_intro')}
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Splash;
